/**
 * Hilfen für den App-Start: benannte Startschritte, Zeitlimit und Fehleranzeige.
 *
 * Das Modul hängt bewusst weder von Vue, i18n, Stores noch von der Konfiguration (`import.meta.env`) ab:
 * Es muss auch dann funktionieren, wenn genau diese Teile beim Start scheitern.
 * Das Markup für Ladehinweis und Fehlerblock steht statisch in `index.html`.
 */

/**
 * Gesamtbudget des Starts bis zum Ergebnis der Rechteermittlung, gerechnet ab Beginn von `setupApp()`.
 * Der Inline-Wächter in `index.html` greift bewusst später (20 s) und hängt als Sicherheitsnetz an diesem Wert.
 */
export const STARTUP_TIMEOUT_MS = 15_000;

/** Schrittname für Fehler, die keinem benannten Startschritt zugeordnet werden können. */
const UNKNOWN_STEP = 'Start';

/** Fehler eines Startschritts, mit dem Namen des Schritts und der ursprünglichen Ursache. */
export class StartupError extends Error {
  readonly step: string;
  /** Ursprüngliche Ursache; als eigenes Feld, weil `tsconfig.vitest.json` ohne `lib` läuft und `ErrorOptions` dort nicht kennt. */
  readonly cause: unknown;

  constructor(step: string, cause: unknown) {
    super(`Startschritt "${step}" fehlgeschlagen: ${describeCause(cause)}`);
    this.name = 'StartupError';
    this.step = step;
    this.cause = cause;
  }
}

function describeCause(cause: unknown): string {
  if (cause instanceof Error) return cause.message || cause.name;
  return String(cause);
}

/** Erkennt Fehler von HTTP-Clients (zum Beispiel `AxiosError`): Sie tragen die Anfrage samt Headern in `config`. */
function isHttpClientError(
  cause: unknown
): cause is { config?: { method?: string; url?: string }; response?: { status?: number } } {
  return (
    typeof cause === 'object' && cause !== null && ('config' in cause || 'response' in cause || 'isAxiosError' in cause)
  );
}

/**
 * Beschreibung der Ursache für die Konsole als Zeichenfolge. Bei Fehlern von HTTP-Clients nur Meldung, HTTP-Status, Methode und URL
 * (ohne Query): `error.config.headers.Authorization` enthält den Anmelde-Token und darf nicht in die Konsole gelangen, aus der
 * der Support Einträge kopiert.
 */
function describeHttpCause(cause: {
  config?: { method?: string; url?: string };
  response?: { status?: number };
}): string {
  const parts: string[] = [];
  if (cause.response?.status !== undefined) parts.push(`HTTP ${cause.response.status}`);
  if (cause.config?.method) parts.push(cause.config.method.toUpperCase());
  if (cause.config?.url) parts.push(cause.config.url.split('?')[0]);
  return parts.length ? `${describeCause(cause)} [${parts.join(' ')}]` : describeCause(cause);
}

/** Führt einen Startschritt aus und ordnet einen Fehler dem Schritt zu. Ein `StartupError` wird nicht doppelt umhüllt. */
export async function runStartupStep<T>(step: string, action: () => Promise<T> | T): Promise<T> {
  try {
    return await action();
  } catch (error) {
    throw error instanceof StartupError ? error : new StartupError(step, error);
  }
}

function timeoutError(step: string): StartupError {
  return new StartupError(step, new Error(`Zeitüberschreitung (Startbudget von ${STARTUP_TIMEOUT_MS / 1000} s)`));
}

/**
 * Begrenzt `promise` auf die Restzeit des Startbudgets (`STARTUP_TIMEOUT_MS` minus die seit `startedAt` verstrichene Zeit).
 * Läuft die Frist ab, lehnt das Ergebnis mit einem `StartupError` für `step` ab. Ist das Budget schon beim Aufruf verbraucht,
 * lehnt es sofort ab, auch wenn `promise` bereits erfüllt ist. Die zugrunde liegende Anfrage wird nicht abgebrochen.
 */
export function withStartupTimeout<T>(
  promise: Promise<T>,
  step: string,
  startedAt: number,
  now: () => number = Date.now
): Promise<T> {
  const remainingMs = Math.max(0, STARTUP_TIMEOUT_MS - (now() - startedAt));

  if (remainingMs === 0) {
    // eine späte Ablehnung des abgelösten Promise soll nicht als unbehandelt gemeldet werden
    promise.catch(() => undefined);
    return Promise.reject(timeoutError(step));
  }

  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(timeoutError(step)), remainingMs);

    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error instanceof StartupError ? error : new StartupError(step, error));
      }
    );
  });
}

/**
 * Protokolliert einen Startfehler in der Browser-Konsole und zeigt den statischen Fehlerblock aus `index.html`.
 * Fehlen die Elemente, bleibt es bei der Konsolenausgabe.
 */
export function reportStartupFailure(error: unknown, doc: Document = document): void {
  const step = error instanceof StartupError ? error.step : UNKNOWN_STEP;
  const cause = error instanceof StartupError ? error.cause : error;

  if (isHttpClientError(cause)) {
    // eslint-disable-next-line no-console
    console.error(`[Start] Schritt "${step}" fehlgeschlagen: ${describeHttpCause(cause)}`);
  } else {
    // das Fehlerobjekt selbst behalten: Der Stacktrace hilft dem Support
    // eslint-disable-next-line no-console
    console.error(`[Start] Schritt "${step}" fehlgeschlagen:`, cause);
  }

  doc.querySelector('[data-testid="startup-loader"]')?.setAttribute('hidden', '');

  const detail = doc.querySelector('[data-testid="startup-error-detail"]');
  if (detail) detail.textContent = `Schritt: ${step} – ${describeCause(cause)}`;

  doc.querySelector('[data-testid="startup-error"]')?.removeAttribute('hidden');
}
